import { PlusOutlined } from "@ant-design/icons";
import { Alert, Button, Checkbox, Form, Input, InputNumber, message, Radio, Select, Space, Typography } from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import {
  DestinacaoPeriodo,
  FluxoPeriodoPreenchido,
  FluxoResposta,
  PeriodoFormValue,
} from "../cadastroCotaUsuario.types";
import { PeriodoCard } from "../components/PeriodoCard";
import { PeriodoForm } from "../components/PeriodoForm";
import { cadastroCotaUsuarioApi } from "../cadastroCotaUsuario.api";
import {
  DEFINICAO_PERIODOS_OPTIONS,
  monthOptions,
  OnboardingFrame,
  SubmitButton,
  formatPeriodoLabel,
  toIsoDate,
  yearOptions,
} from "../utils";

interface Props {
  fluxo: FluxoResposta;
  submitting: boolean;
  onConcluir: (payload: Record<string, unknown>) => Promise<void>;
}

interface EtapaPeriodosFormValues {
  definicaoPeriodos?: string;
  descricaoDefinicaoPeriodos?: string;
  numeroCota?: number;
  naoSeiNumeroCota?: boolean;
  periodos?: Array<
    PeriodoFormValue & {
      periodoId?: number;
    }
  >;
  periodosJaDefinidos?: boolean;
  mes?: number;
  ano?: number;
}

// Datas de check-in/check-out são datas civis, não instantes no tempo.
// Remover o horário/UTC evita que meia-noite em UTC vire o dia anterior no Brasil.
function parseDateOnly(value?: string | null) {
  return value ? dayjs(value.slice(0, 10)) : undefined;
}

function destinacaoParaFormulario(
  destinacao?: DestinacaoPeriodo | string
): DestinacaoPeriodo | undefined {
  switch (destinacao) {
    case "VOU_UTILIZAR":
    case "UTILIZAR":
      return "USO_PROPRIO";
    case "VOU_EMPRESTAR":
    case "EMPRESTADO":
    case "EMPRESTAR":
    case "EMPRESTEI":
    case "CEDIDO":
    case "VOU_CEDER":
      return "CEDIDO_EMPRESTADO";
    case "ALUGUEI_POR_CONTA_PROPRIA":
      return "NEGOCIACAO_PROPRIA";
    default:
      return destinacao as DestinacaoPeriodo | undefined;
  }
}

function periodoJaDefinidoDoBackend(dadosPreenchidos?: Record<string, unknown>) {
  const dados = dadosPreenchidos as
      | {
        periodoJaDefinido?: boolean;
        periodoNaoDefinido?: boolean;
        periodosJaDefinidos?: boolean;
        periodosAindaNaoDefinidos?: boolean;
      }
    | undefined;
  const existencia = dadosPreenchidos?.existenciaPeriodos as
    | {
        periodoJaDefinido?: boolean;
        periodoNaoDefinido?: boolean;
        periodosJaDefinidos?: boolean;
        periodosAindaNaoDefinidos?: boolean;
      }
    | undefined;

  if (typeof (existencia?.periodoJaDefinido ?? dados?.periodoJaDefinido) === "boolean") {
    return existencia?.periodoJaDefinido ?? dados?.periodoJaDefinido;
  }
  if (typeof existencia?.periodosJaDefinidos === "boolean") {
    return existencia.periodosJaDefinidos;
  }
  if (typeof existencia?.periodosAindaNaoDefinidos === "boolean") {
    return !existencia.periodosAindaNaoDefinidos;
  }
  if (typeof (existencia?.periodoNaoDefinido ?? dados?.periodoNaoDefinido) === "boolean") {
    return !(existencia?.periodoNaoDefinido ?? dados?.periodoNaoDefinido);
  }
  return undefined;
}

export function EtapaPeriodos({ fluxo, submitting, onConcluir }: Props) {
  const [form] = Form.useForm<EtapaPeriodosFormValues>();
  const [periodosBuscados, setPeriodosBuscados] = useState<FluxoPeriodoPreenchido[]>([]);

  console.log("[EtapaPeriodos] Dados recebidos do backend:", {
    fluxo,
    etapaAtual: fluxo.etapaAtual,
    trilha: fluxo.trilha,
    contextoFluxo: fluxo.contextoFluxo,
    dadosPreenchidos: fluxo.dadosPreenchidos,
    detalhesEtapa: fluxo.detalhesEtapa,
  });

  // Os detalhes retornados pela API pertencem somente à etapa atual do fluxo.
  const detalhes = fluxo.etapaAtual === "AGUARDANDO_PERIODOS"
    ? fluxo.detalhesEtapa
    : undefined;
  const periodosPreenchidos = useMemo(
    () => (fluxo.dadosPreenchidos?.periodos as FluxoPeriodoPreenchido[] | undefined) ?? [],
    [fluxo.dadosPreenchidos?.periodos]
  );
  const pergunta = detalhes?.pergunta;
  const exibindoDefinicaoPeriodos =
    fluxo.noAtual === "COMO_FUNCIONA_DEFINICAO_PERIODOS" ||
    pergunta === "COMO_FUNCIONA_DEFINICAO_PERIODOS";
  const subtela = fluxo.noAtual === "EXISTENCIA_PERIODOS"
    ? "EXISTENCIA_PERIODOS"
    : fluxo.noAtual === "COMO_FUNCIONA_DEFINICAO_PERIODOS"
      ? "COMO_FUNCIONA_DEFINICAO_PERIODOS"
    : fluxo.noAtual === "INFORMAR_PERIODOS"
      ? "INFORMAR_PERIODOS"
      : fluxo.noAtual === "LEMBRETE_PERIODOS"
        ? "LEMBRETE_PERIODOS"
        : fluxo.noAtual === "PERIODOS"
          ? "EXISTENCIA_PERIODOS"
          : pergunta === "PERIODOS_JA_DEFINIDOS"
            ? "EXISTENCIA_PERIODOS"
            : undefined;
  const modoPeriodo = detalhes?.modoPeriodo ?? fluxo.contextoFluxo.modoPeriodo ??
    (periodosPreenchidos.length ? "APRESENTAR_PERIODOS" : "INFORMAR_PERIODOS");
  const dadosDisponibilidade = fluxo.dadosPreenchidos?.disponibilidade as
    { escopoDisponibilizacao?: string } | undefined;
  const escopo = detalhes?.escopoDisponibilizacao ??
    dadosDisponibilidade?.escopoDisponibilizacao ?? fluxo.contextoFluxo.escopoDisponibilizacao;
  const periodos = useMemo(
    () => (periodosPreenchidos.length
      ? periodosPreenchidos.map((periodo) => ({
          ...periodo,
          id: periodo.periodoId,
        }))
      : detalhes?.periodos ?? periodosBuscados.map((periodo) => ({
          ...periodo,
          id: periodo.periodoId,
        })))
      .map((periodo) => {
        const periodoId = periodo.periodoId ?? periodo.id;
        const preenchido = periodosPreenchidos.find(
          (item) => item.periodoId === periodoId
        );

        return {
          ...periodo,
          // A informação persistida em dadosPreenchidos é a fonte principal.
          editavel: preenchido?.editavel ?? periodo.editavel,
        };
      })
      .filter((periodo) => parseDateOnly(periodo.checkIn)?.isAfter(dayjs(), "day")),
    [detalhes?.periodos, periodosBuscados, periodosPreenchidos]
  );

  useEffect(() => {
    const cotaUsuarioId = fluxo.contextoFluxo.cotaUsuarioId ?? fluxo.cotaUsuarioId;
    if (subtela !== "INFORMAR_PERIODOS") return;

    if (periodosPreenchidos.length) {
      form.setFieldsValue({
        periodos: periodosPreenchidos.map((periodo) => ({
          periodoId: periodo.periodoId,
          checkIn: parseDateOnly(periodo.checkIn),
          checkOut: parseDateOnly(periodo.checkOut),
          editavel: periodo.editavel,
          destinacao: destinacaoParaFormulario(periodo.destinacao),
          receberOportunidades:
            periodo.destinacao === "OPORTUNIDADES_MULTICOTAS",
        })),
      });
      return;
    }

    if (!cotaUsuarioId) return;

    let ativo = true;
    cadastroCotaUsuarioApi.buscarPeriodosPorCotaUsuario(cotaUsuarioId).then((periodosApi) => {
      if (!ativo) return;
      setPeriodosBuscados(periodosApi.map((periodo) => ({
        periodoId: periodo.periodoId,
        checkIn: periodo.checkIn,
        checkOut: periodo.checkOut,
        editavel: periodo.editavel,
      })));
      form.setFieldsValue({
        periodos: periodosApi.length
          ? periodosApi.map((periodo) => ({
              periodoId: periodo.periodoId,
              checkIn: parseDateOnly(periodo.checkIn),
              checkOut: parseDateOnly(periodo.checkOut),
              editavel: periodo.editavel,
            }))
          : [{}],
      });
    });

    return () => { ativo = false; };
  }, [fluxo.contextoFluxo.cotaUsuarioId, fluxo.cotaUsuarioId, form, periodosPreenchidos, subtela]);

  const definicaoPeriodos = Form.useWatch("definicaoPeriodos", form);
  const naoSeiNumeroCota = Form.useWatch("naoSeiNumeroCota", form);
  const dadosLembrete = useMemo(
    () => ({
      ...fluxo.contextoFluxo,
      ...fluxo.dadosPreenchidos,
    }) as { mes?: number; ano?: number; lembrarEm?: string | null },
    [fluxo.contextoFluxo, fluxo.dadosPreenchidos]
  );
  const lembrarEm = useMemo(
    () => (dadosLembrete.lembrarEm ? dayjs(dadosLembrete.lembrarEm) : undefined),
    [dadosLembrete.lembrarEm]
  );

  useEffect(() => {
    if (subtela === "EXISTENCIA_PERIODOS") {
      form.setFieldsValue({
        periodosJaDefinidos:
          periodoJaDefinidoDoBackend(fluxo.dadosPreenchidos),
      });
      return;
    }

    if (subtela === "LEMBRETE_PERIODOS") {
      form.setFieldsValue({
        mes: dadosLembrete?.mes ?? (lembrarEm?.isValid() ? lembrarEm.month() + 1 : undefined),
        ano: dadosLembrete?.ano ?? (lembrarEm?.isValid() ? lembrarEm.year() : undefined),
      });
      return;
    }

    if (exibindoDefinicaoPeriodos) {
      const dadosDefinicao = fluxo.dadosPreenchidos?.definicaoPeriodos as
        | string
        | {
            definicaoPeriodos?: string;
            descricaoDefinicaoPeriodos?: string;
            descricao?: string;
            numeroCota?: number | null;
            numeroCotaConfirmado?: boolean;
          }
        | undefined;

      const possuiNumeroCotaPreenchido =
        typeof dadosDefinicao === "object" &&
        Object.prototype.hasOwnProperty.call(dadosDefinicao, "numeroCota");
      const possuiConfirmacaoPreenchida =
        typeof dadosDefinicao === "object" &&
        Object.prototype.hasOwnProperty.call(dadosDefinicao, "numeroCotaConfirmado");
      const numeroCota = possuiNumeroCotaPreenchido
        ? dadosDefinicao?.numeroCota
        : fluxo.contextoFluxo.numeroCota;
      const numeroCotaConfirmado = possuiConfirmacaoPreenchida
        ? dadosDefinicao?.numeroCotaConfirmado
        : fluxo.contextoFluxo.numeroCotaConfirmado;
      const definicao =
        typeof dadosDefinicao === "string"
          ? dadosDefinicao
          : dadosDefinicao?.definicaoPeriodos ??
            (numeroCota !== undefined || numeroCotaConfirmado === true
              ? "CRONOGRAMA_FIXO"
              : undefined);
      const descricao =
        typeof dadosDefinicao === "string"
          ? undefined
          : dadosDefinicao?.descricaoDefinicaoPeriodos ?? dadosDefinicao?.descricao;

      form.setFieldsValue({
        definicaoPeriodos: definicao,
        descricaoDefinicaoPeriodos: descricao,
        numeroCota: numeroCota ?? undefined,
        naoSeiNumeroCota:
          numeroCota === null && numeroCotaConfirmado === true,
      });
      return;
    }

    if (
      pergunta === "QUAL_E_O_NUMERO_DA_COTA" &&
      subtela !== "INFORMAR_PERIODOS"
    ) {
      form.setFieldsValue({
        numeroCota: undefined,
        naoSeiNumeroCota: false,
      });
      return;
    }

    // Nesta subetapa, a lista é inicializada pelo Form/Form.List e os dados
    // existentes são preenchidos pelo efeito que consulta a API. Não reescreva
    // a lista aqui, pois isso apagaria a data que o usuário acabou de escolher.
    if (subtela === "INFORMAR_PERIODOS") return;

    if (modoPeriodo === "APRESENTAR_PERIODOS") {
      form.setFieldsValue({
        periodos: periodos.map((periodo) => {
          const periodoId = periodo.periodoId ?? periodo.id;
          const destinacao = periodosPreenchidos.find(
            (preenchido) => preenchido.periodoId === periodoId
          )?.destinacao;
          return {
            periodoId,
            destinacao: destinacaoParaFormulario(destinacao),
            receberOportunidades: destinacao
              ? destinacao === "OPORTUNIDADES_MULTICOTAS"
              : undefined,
          };
        }),
      });
      return;
    }

    form.setFieldsValue({
      periodos:
        periodos.length
          ? periodos.map((periodo) => ({
              checkIn: parseDateOnly(periodo.checkIn),
              checkOut: parseDateOnly(periodo.checkOut),
              destinacao: periodosPreenchidos.find(
                (preenchido) => preenchido.periodoId === (periodo.periodoId ?? periodo.id)
              )?.destinacao,
            }))
          : [{}],
      mes: undefined,
      ano: undefined,
    });
  }, [dadosLembrete, exibindoDefinicaoPeriodos, fluxo.dadosPreenchidos, form, lembrarEm, modoPeriodo, periodos, periodosPreenchidos, pergunta, subtela]);

  function submit(values: EtapaPeriodosFormValues) {
    if (subtela === "EXISTENCIA_PERIODOS") {
      onConcluir({ periodosJaDefinidos: values.periodosJaDefinidos });
      return;
    }

    if (subtela === "LEMBRETE_PERIODOS") {
      onConcluir({
        // Compatibilidade com o contrato atual da API: esta resposta conclui
        // a etapa sem cadastrar períodos e agenda o lembrete para as datas.
        periodosJaDefinidos: false,
        mes: values.mes,
        ano: values.ano,
      });
      return;
    }

    if (exibindoDefinicaoPeriodos) {
      onConcluir({
        definicaoPeriodos: values.definicaoPeriodos,
        ...(values.definicaoPeriodos === "OUTRO"
          ? { descricaoDefinicaoPeriodos: values.descricaoDefinicaoPeriodos }
          : {}),
        ...(values.definicaoPeriodos === "CRONOGRAMA_FIXO"
          ? values.naoSeiNumeroCota
            ? { naoSeiNumeroCota: true }
            : { numeroCota: values.numeroCota }
          : {}),
      });
      return;
    }

    if (
      pergunta === "QUAL_E_O_NUMERO_DA_COTA" &&
      subtela !== "INFORMAR_PERIODOS"
    ) {
      onConcluir(
        values.naoSeiNumeroCota
          ? { naoSeiNumeroCota: true }
          : { numeroCota: values.numeroCota }
      );
      return;
    }

    if (subtela !== "INFORMAR_PERIODOS" && modoPeriodo === "APRESENTAR_PERIODOS") {
      const periodosComDestino = (values.periodos ?? []).map((periodo) => {
        const destinacao =
          periodo.receberOportunidades === true
            ? "OPORTUNIDADES_MULTICOTAS"
            : periodo.destinacao;

        return {
          periodoId: periodo.periodoId,
          destinacao,
        };
      });

      const periodoSemDestino = periodosComDestino.find(
        (periodo) => !periodo.periodoId || !periodo.destinacao
      );

      if (escopo !== "TODOS_OS_PERIODOS" && periodoSemDestino) {
        message.error(
          `Informe a destinação do período ${periodoSemDestino.periodoId ?? "selecionado"}.`
        );
        return;
      }

      onConcluir({
        periodos:
          escopo === "TODOS_OS_PERIODOS"
            ? periodos.map((periodo) => ({
                periodoId: periodo.periodoId ?? periodo.id,
                destinacao: "OPORTUNIDADES_MULTICOTAS",
              }))
            : periodosComDestino,
      });
      return;
    }

    const periodosNormalizados = (values.periodos ?? []).map((periodo) => ({
      periodoId: periodo.periodoId,
      checkIn: toIsoDate(periodo.checkIn),
      checkOut: toIsoDate(periodo.checkOut),
      destinacao:
        periodo.receberOportunidades === true
          ? "OPORTUNIDADES_MULTICOTAS"
          : periodo.destinacao,
    }));

    console.log("[EtapaPeriodos] Payload normalizado antes da validação", {
      periodos: periodosNormalizados,
    });

    const periodoSemDatas = periodosNormalizados.find(
      (periodo) => !periodo.checkIn || !periodo.checkOut
    );
    if (periodoSemDatas) {
      message.error("Informe o check-in e o check-out de cada período.");
      return;
    }

    const periodoInvalido = periodosNormalizados.find(
      (periodo) =>
        !periodo.checkIn ||
        !periodo.checkOut ||
        new Date(periodo.checkIn) < dayjs().startOf("day").toDate() ||
        new Date(periodo.checkOut) <= new Date(periodo.checkIn) ||
        dayjs(periodo.checkOut).diff(dayjs(periodo.checkIn), "day") > 7
    );

    if (periodoInvalido) {
      message.error(
        "O período deve durar no máximo 7 dias."
      );
      return;
    }

    onConcluir({
      periodos: periodosNormalizados.map((periodo) =>
        escopo === "TODOS_OS_PERIODOS"
          ? {
              ...(periodo.periodoId ? { periodoId: periodo.periodoId } : {}),
              checkIn: periodo.checkIn,
              checkOut: periodo.checkOut,
            }
          : periodo
      ),
    });
  }

  return (
    <OnboardingFrame
      fluxo={fluxo}
      title="Para quais períodos gostaria de receber oportunidades?"
      description="Escolha os períodos para os quais deseja receber oportunidades de aluguel ou troca."
    >
      <Form
        layout="vertical"
        form={form}
        initialValues={{ periodos: [{}] }}
        onFinish={submit}
      >
        {subtela === "EXISTENCIA_PERIODOS" ? (
          <Form.Item
            label="Você já sabe quais são os seus períodos?"
            name="periodosJaDefinidos"
            rules={[{ required: true, message: "Selecione uma opção." }]}
          >
            <Radio.Group
              options={[
                { label: "Sim, já tenho meus períodos definidos", value: true },
                { label: "Ainda não tenho meus períodos definidos", value: false },
              ]}
              style={{ display: "grid", gap: 12 }}
            />
          </Form.Item>
        ) : null}

        {subtela === "LEMBRETE_PERIODOS" ? (
          <Space direction="vertical" size={16} style={{ display: "flex" }}>
            <Typography.Text>Quando devemos lembrar você?</Typography.Text>
            <Typography.Paragraph>
              Podemos lembrar você de cadastrar seus períodos quando as datas já estiverem disponíveis.
            </Typography.Paragraph>
            <Form.Item label="Mês" name="mes" rules={[{ required: true, message: "Selecione o mês." }]}>
              <Select options={monthOptions} placeholder="Selecione o mês" />
            </Form.Item>
            <Form.Item label="Ano" name="ano" rules={[{ required: true, message: "Selecione o ano." }]}>
              <Select options={yearOptions} placeholder="Selecione o ano" />
            </Form.Item>
          </Space>
        ) : null}

        {exibindoDefinicaoPeriodos &&
        subtela !== "EXISTENCIA_PERIODOS" ? (
          <>
            <Form.Item
              label="Como funciona a definição dos seus períodos de hospedagem?"
              name="definicaoPeriodos"
              rules={[{ required: true, message: "Selecione uma opção." }]}
            >
              <Radio.Group style={{ display: "grid", gap: 12, width: "100%" }}>
                {DEFINICAO_PERIODOS_OPTIONS.map((option) => {
                  const descricao =
                    option.value === "CRONOGRAMA_FIXO"
                      ? "Seus períodos seguem um calendário definido, permitindo saber com antecedência quais serão as suas datas de hospedagem, inclusive nos próximos anos."
                      : option.value === "ESCOLHA_PERIODICA"
                        ? "Você escolhe seus períodos de hospedagem periodicamente, conforme as regras e a disponibilidade do empreendimento."
                        : "A definição dos seus períodos funciona de uma maneira diferente das opções acima.";

                  return (
                    <Radio key={option.value} value={option.value}>
                      <span style={{ display: "inline-grid", gap: 4 }}>
                        <Typography.Text strong>
                          {option.value === "OUTRO" ? "Outro formato" : option.label}
                        </Typography.Text>
                        <Typography.Text type="secondary">{descricao}</Typography.Text>
                      </span>
                    </Radio>
                  );
                })}
              </Radio.Group>
            </Form.Item>

            {/* {definicaoPeriodos ? (
              <Typography.Text type="secondary">
                {definicaoPeriodos === "CRONOGRAMA_FIXO"
                  ? "A cobrança da cota será aplicada para esta opção."
                  : "Não haverá cobrança da cota para esta opção."}
              </Typography.Text>
            ) : null} */}

            {definicaoPeriodos === "CRONOGRAMA_FIXO" ? (
              <>
                <Form.Item
                  label="Número da cota"
                  name="numeroCota"
                  rules={
                    naoSeiNumeroCota
                      ? []
                      : [{ required: true, message: "Informe o número da cota." }]
                  }
                >
                  <InputNumber
                    min={1}
                    disabled={naoSeiNumeroCota}
                    placeholder="Ex.: 21"
                    style={{ width: "100%" }}
                  />
                </Form.Item>

                <Form.Item name="naoSeiNumeroCota" valuePropName="checked">
                  <Checkbox>Não sei o número da minha cota</Checkbox>
                </Form.Item>
              </>
            ) : null}

            {definicaoPeriodos === "OUTRO" ? (
              <Form.Item
                label="Descreva como seus períodos são definidos"
                name="descricaoDefinicaoPeriodos"
                rules={[{ required: true, message: "Descreva a definição." }]}
              >
                <Input.TextArea rows={4} />
              </Form.Item>
            ) : null}
          </>
        ) : null}

        {pergunta === "QUAL_E_O_NUMERO_DA_COTA" &&
        subtela !== "EXISTENCIA_PERIODOS" &&
        subtela !== "INFORMAR_PERIODOS" ? (
          <>
            <Form.Item name="naoSeiNumeroCota" valuePropName="checked">
              <Checkbox>Não sei o número da minha cota</Checkbox>
            </Form.Item>

            <Form.Item
              label="Número da cota"
              name="numeroCota"
              rules={
                naoSeiNumeroCota
                  ? []
                  : [{ required: true, message: "Informe o número da cota." }]
              }
            >
              <InputNumber
                min={1}
                disabled={naoSeiNumeroCota}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </>
        ) : null}

        {subtela !== "EXISTENCIA_PERIODOS" &&
        subtela !== "LEMBRETE_PERIODOS" &&
        subtela !== "INFORMAR_PERIODOS" &&
        !pergunta &&
        modoPeriodo === "APRESENTAR_PERIODOS" ? (
          <>
            {escopo === "APENAS_ALGUNS_PERIODOS" && periodos.length ? (
              <Space
                align="center"
                size={12}
                wrap
                style={{ display: "flex", marginBottom: 20 }}
              >
                <Typography.Text strong>Quer receber oportunidades para todos?</Typography.Text>
                <Button
                  onClick={() =>
                    form.setFieldsValue({
                      periodos: periodos.map((periodo) => ({
                        periodoId: periodo.periodoId ?? periodo.id,
                        receberOportunidades: true,
                        destinacao: "OPORTUNIDADES_MULTICOTAS",
                      })),
                    })
                  }
                >
                  Marcar todos como Sim
                </Button>
                <Typography.Text type="secondary">
                  Você poderá alterar individualmente as exceções.
                </Typography.Text>
              </Space>
            ) : null}

            {!periodos.length ? (
              <Alert
                type="warning"
                showIcon
                message="Não há períodos futuros disponíveis para apresentação."
                style={{ marginBottom: 16 }}
              />
            ) : null}

            {(periodos ?? []).map((periodo, index) => (
              <PeriodoCard
                key={periodo.id}
                index={index}
                periodo={periodo}
                fieldName={index}
                namePathPrefix={["periodos"]}
                solicitarDestinacao={escopo === "APENAS_ALGUNS_PERIODOS"}
              />
            ))}
          </>
        ) : null}

        {subtela === "INFORMAR_PERIODOS" ? (
          <>
            <Form.List name="periodos">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map((field, index) => {
                      const periodoId = form.getFieldValue([
                        "periodos",
                        field.name,
                        "periodoId",
                      ]);
                      const periodoPreenchido = periodosPreenchidos.find(
                        (periodo) => periodo.periodoId === periodoId
                      ) ?? periodosPreenchidos[index];

                      return (
                        <PeriodoForm
                          key={field.key}
                          index={index}
                          fieldName={field.name}
                          periodoLabel={
                            periodoPreenchido
                              ? formatPeriodoLabel(
                                  periodoPreenchido.checkIn,
                                  periodoPreenchido.checkOut
                                )
                              : undefined
                          }
                          editavel={periodoPreenchido?.editavel}
                          receberOportunidadesInicial={
                            form.getFieldValue(["periodos", field.name, "receberOportunidades"])
                          }
                          solicitarDatas
                          solicitarDestinacao={escopo === "APENAS_ALGUNS_PERIODOS"}
                          showRemove={fields.length > 1}
                          onRemove={() => remove(field.name)}
                        />
                      );
                    })}

                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={() => add({})}
                      style={{ width: "100%", marginBottom: 24 }}
                    >
                      Adicionar outro período
                    </Button>
                  </>
                )}
              </Form.List>
          </>
        ) : null}

        <SubmitButton label="Continuar" loading={submitting} />
      </Form>
    </OnboardingFrame>
  );
}
